import { IIsiDtoPresentationLogicRequest } from "../../Dtos/Isilog.IsiComponent.IsiDataTransferObject";
import { IIsiDtoHttpHandlerError } from "../../Dtos/Isilog.IsiFramework.IsiDataTransferObject";
import { IsiPageRequestManagerRequest } from "../../IsiWebGUI/IsiFW/Core/Manager/IsiPageRequestManagerRequest";
import { IIsiUIFlowResultGeneric } from "../Core/IIsiUIFlowResultGeneric";

export class IsiPresentationLogic {

    // =============================
    // Private fields
    // =============================

    private _ashxName: string;
    /**
     * Liste des actions annulables.
     */
    private _abortableAction = new Map<string, IsiPageRequestManagerRequest>() ;


    // =============================
    // Constructor
    // =============================

    constructor(name: string) {
        this._ashxName = name + ".ashx";
    }



    // =============================
    // Protected methods
    // =============================

    protected ajax<TRequest extends IIsiDtoPresentationLogicRequest>(action: string, data: TRequest, isBlocking: boolean): Promise<TRequest> {

        return this.baseAjax(
            action,
            data,
            isBlocking,
            (resolve) => () => resolve(data),
            (resolve, reject) => () => reject(data),
            false,
            null
        );
    }

    /**
     * Appel ajax qui, si le serveur retourne une erreur, affiche le message d'erreur
     * 
     * @param action L'action du handler à appeler
     * @param data Le DTO à envoyer au serveur
     * 
     * @returns le flow retourné contient :
     * - 'result' et 'continue' à false en cas d'erreur, à true sinon
     * - 'reponse' = request retournée par le handler du serveur
     */
    protected ajaxWithErrorHandling<TRequest extends IIsiDtoPresentationLogicRequest>(action: string, data: TRequest, isBlocking: boolean): Promise<IIsiUIFlowResultGeneric<TRequest>> {

        return this.baseAjax(
            action,
            data,
            isBlocking,
            (resolve) => () => resolve({ result: true, continue: true, reponse: data }),
            (resolve, reject) => () => {
                const error: IIsiDtoHttpHandlerError = data?.response?.error;
                if (error?.errorMessage != null) {
                    this.showErrorOverridable(error.errorMessage);
                } else {
                    uiCore.logError("Request failed with no error message.");
                }
                resolve({ result: false, continue: false, reponse: data });
            },
            false,
            null
        );
    }

    /**
     * Appel ajax qui, si le serveur retourne une erreur, affiche le message d'erreur dans la console
     * 
     * @param action L'action du handler à appeler
     * @param data Le DTO à envoyer au serveur
     * 
     * @returns le flow retourné contient :
     * - 'result' et 'continue' à false en cas d'erreur, à true sinon
     * - 'reponse' = request retournée par le handler du serveur
     */
    protected ajaxWithErrorLoggin<TRequest extends IIsiDtoPresentationLogicRequest>(action: string, data: TRequest, isBlocking: boolean): Promise<IIsiUIFlowResultGeneric<TRequest>> {

        return this.baseAjax(
            action,
            data,
            isBlocking,
            (resolve) => () => resolve({ result: true, continue: true, reponse: data }),
            (resolve, reject) => () => {
                const error: IIsiDtoHttpHandlerError = data?.response?.error;
                if (error?.errorMessage != null) {
                    uiCore.logError(error.errorMessage);
                } else {
                    uiCore.logError("Request failed with no error message.");
                }
                resolve({ result: false, continue: false, reponse: data });
            },
            false,
            null
        );
    }

    /**
    * Appel ajax qui, si le serveur retourne une erreur, affiche le message d'erreur dans la console
    * 
    * @param action L'action du handler à appeler
    * @param data Le DTO à envoyer au serveur
    * 
    * @returns le flow retourné contient :
    * - 'result' et 'continue' à false en cas d'erreur, à true sinon
    * - 'reponse' = request retournée par le handler du serveur
    */
    protected abortableAjaxActionWithErrorLoggin<TRequest extends IIsiDtoPresentationLogicRequest>(contextId: string, action: string, data: TRequest, isBlocking: boolean): Promise<IIsiUIFlowResultGeneric<TRequest>> {

        return this.baseAjax(
            action,
            data,
            isBlocking,
            (resolve) => () => resolve({ result: true, continue: true, reponse: data }),
            (resolve, reject) => () => {
                const error: IIsiDtoHttpHandlerError = data?.response?.error;
                if (error?.errorMessage != null) {
                    uiCore.logError(error.errorMessage);
                } else {
                    uiCore.logError("Request failed with no error message.");
                }
                resolve({ result: false, continue: false, reponse: data });
            },
            true,
            contextId
        );
    }
    /**
     * annule une requête si elle existe.
     * @param action
     */
    protected abortActionIfExist(contextId: string, action: string) {
        this._abortRequest(contextId + action);
    }
    /**
     * Annule une requête donnée.
     */
    private _abortRequest(id: string) {
        if (this._abortableAction.has(id) && this._abortableAction.get(id)?.JAjaxRequest?.state() === "pending") {
            try {
                this._abortableAction.get(id).JAjaxRequest.abort();
            } catch (es) {
                uiCore.log(`Annulation de la requête http liée à l'action :${id}`);
            }
        }
    }
    /**
     * Annule toutes les requêtes du pl annulables
     */
    public abortAllCurrentActions() {
        for (const key of this._abortableAction.keys()) {
            this._abortRequest(key);
        }
    }

    /**
     * Méthode surchargeable d'affichage d'erreurs
     * */
    protected showErrorOverridable(error: string) {
        uiCore.showErrorMessage(error);
    }



    // =============================
    // Private methods
    // =============================

    private baseAjax<TRequest extends IIsiDtoPresentationLogicRequest, TReturn>(action: string, data: TRequest, isBlocking: boolean, callback: (resolve) => (() => void), errorCallback: (resolve, reject) => (() => void), abortable: boolean = false, contextId:string): Promise<TReturn> {
        return new Promise((resolve, reject) => {
            const request = uiCore.pageRequestManager.IsiCreateAjaxRequest
                (
                    this,
                    {
                        callback: callback(resolve),
                        errorCallback: errorCallback(resolve, reject)                      
                    },
                    { IsiData: data },
                    "POST",
                    uiCore.buildAjaxHandlerUrl(this._ashxName, action),
                    true,
                    isBlocking
            );
            if (abortable) {
                this._abortableAction.set(contextId + action, request);
            }
            
            uiCore.pageRequestManager.doRequest(request);
        });
    }
}